import * as Sentry from "@sentry/react";

import { Paragraph, PaddedSurface as Surface } from "App/global-style";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { CAR_ABOUT_STEP, CAR_FIND_STEP, CAR_PRICE_STEP } from "../carSteps";
import { CarProductDetailsFormValues } from "./FormElements/interfaces";

import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { useUserIdentification } from "Components/Features/CheckPrice/userIdentificationHook";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { DamageRecentYears } from "Components/Form/RecentDamages/recentDamagesInterfaces";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { BonusValue } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductDetails/FormElements/Bonus/constant";
import { initialPriceFormState } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/CarProductPrice";
import { CarPriceFormValues } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/interfaces";
import { BonusTransfer } from "Store/interfaces/CompanyTransferSpecifications";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { CarPriceRequestInfo } from "Store/interfaces/products/checkPrice/riskProducts/VehicleProducts/Car";
import { WarrantyType } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/BaseVehicleProduct";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { mapValidationErrors } from "Utils/formUtils";
import React from "react";
import { CarProductIntroFormValues } from "../ProductIntro/FormElements/interfaces";
import { mapFormToPriceRequest } from "../utils/mapFormToPriceRequest";
import { FormElements } from "./FormElements";
import { validationSchema } from "./validation";

type Props = {
  updatePriceRequest: (payload: CarPriceRequestInfo) => void;
};

export const CarProductDetails = ({
  updatePriceRequest,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const userIdentification = useUserIdentification(ProductBackendName.CAR);
  const introFormState = useFormState(
    CAR_FIND_STEP.id,
  ) as Required<CarProductIntroFormValues>;
  const priceFormState = useFormState(
    CAR_PRICE_STEP.id,
    initialPriceFormState,
  ) as CarPriceFormValues;
  const age = Number(userIdentification.current?.newCustomerResponse.age) ?? 0;
  const initialFormState = useFormState(
    CAR_ABOUT_STEP.id,
    initialFormAboutValues(age),
  );

  const { getText, createLinkComponent } = useTexts();
  const [
    {
      formText: { aboutCar },
      details,
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.CAR));
  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const handleSubmit = (formValues: CarProductDetailsFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: CAR_ABOUT_STEP.id,
        formState: {
          ...formValues,
          automaticallyPopulated: false,
        },
      }),
    );
    updatePriceRequest(
      mapFormToPriceRequest(
        userIdentification.current,
        introFormState,
        formValues,
        priceFormState,
      ),
    );
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.CAR}`,
        );
      }}
    >
      <ProductDetailsTile
        header={getText("pageheader.car.productdetails")}
        validationSchema={validationSchema(getText)}
        initialValues={{ ...initialFormState }}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<CarProductDetailsFormValues>) => {
          return (
            <Form>
              <Surface variant="border" maxWidth="376px">
                <p>
                  <b>
                    {introFormState.brand} {introFormState.model}{" "}
                    {introFormState.year}
                  </b>
                  {introFormState.licensePlateNumber && (
                    <>
                      <br />
                      <span>{introFormState.licensePlateNumber}</span>
                    </>
                  )}
                </p>
              </Surface>
              <Paragraph>
                {createLinkComponent("carform.ingress.bonus", {}, false)}
              </Paragraph>
              <FormElements
                isCompanyChange={
                  introFormState.warrantyType === WarrantyType.COMPANY_CHANGE
                }
                formProps={formProps}
                texts={aboutCar}
                lang={lang}
              />
              {priceCalculationResult.error && (
                <SystemErrorAlert alertId="priceCalculation" />
              )}
              <ErrorsSummedUp
                errors={mapValidationErrors(formProps.errors, details, lang)}
                isVisible
              />

              <ScrollToFirstError />

              <NavigationButtons
                isLoading={priceCalculationResult.isLoading}
                disableNextButton={formProps.values.bonus === "07"}
              />
            </Form>
          );
        }}
      </ProductDetailsTile>
    </Sentry.ErrorBoundary>
  );
};

export const initialFormAboutValues = (
  age: number,
): CarProductDetailsFormValues => ({
  bonusPreOwnership: BonusTransfer.MY_OWN,
  bonus: getInitialBonusForCustomer(age),
  injuries: DamageRecentYears.NONE,
  mortgagee: BooleanFormType.FALSE,
  injuryYear: undefined,
  automaticallyPopulated: false,
});

export const getInitialBonusForCustomer = (age: number): string => {
  if (!age || age <= 25) {
    return BonusValue.SIXTY;
  } else if (age <= 30) {
    return BonusValue.SEVENTY;
  } else {
    return BonusValue.SEVENTYFIVE;
  }
};
